import { deleteCookie, getCookie, sleep } from 'utils/dom'
import { TOKEN_LOCAL_STORAGE_KEY } from 'codegen/fetcher/fetcher.utils'
import type { UserQuery, LogInMutation, RegisterMutation } from 'codegen/generated/user'
import { WORDPRESS_LOGIN_COOKIE_NAME } from 'constants/cookies'
import { localStore } from 'utils/storage'

const GRAPHQL_URL =
  process.env.NEXT_PUBLIC_GRAPHQL_URL ||
  process.env.VITE_GRAPHQL_URL ||
  process.env.GRAPHQL_URL_SELF_HOSTED

type AuthOrigin = 'login' | 'registration'

type JwtAuthResult = 'OK' | 'HTTP_ERROR' | 'NETWORK_ERROR' | 'RETRY_EXHAUSTED' | 'SKIPPED_DOMAIN'

const REPORT_JWT_AUTH_OUTCOME_MUTATION = `
  mutation reportJwtAuthOutcome($input: JwtAuthOutcomeInput!) {
    reportJwtAuthOutcome(input: $input) { accepted }
  }
`

function reportJwtAuthOutcome(
  origin: AuthOrigin,
  result: JwtAuthResult,
  httpStatus?: number,
  attempts?: number,
) {
  if (!GRAPHQL_URL || typeof window === 'undefined') {
    return
  }
  const input: Record<string, unknown> = {
    origin: origin.toUpperCase(),
    result,
  }
  if (typeof httpStatus === 'number') {
    input.httpStatus = httpStatus
  }
  if (typeof attempts === 'number') {
    input.attempts = attempts
  }

  // fire-and-forget: never let telemetry block the auth flow
  fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      operationName: 'reportJwtAuthOutcome',
      query: REPORT_JWT_AUTH_OUTCOME_MUTATION,
      variables: { input },
    }),
    keepalive: true,
  }).catch(() => {
    // swallow — telemetry failures must not affect the user
  })
}

// Use the actual generated types instead of creating our own
export type UserAuthResponse =
  | NonNullable<UserQuery['me']>
  | NonNullable<LogInMutation['login']>
  | NonNullable<RegisterMutation['register']>

const deviceIdCookieName = 'cfAmpAnonymousId'
const deviceIdUndefined = '__notSet__'

const CF_URL = process.env.NEXT_PUBLIC_CF_URL || process.env.VITE_CF_URL

export function getDeviceId() {
  if (typeof document === 'undefined') {
    return deviceIdUndefined
  }

  return getCookie(deviceIdCookieName) || deviceIdUndefined
}

export function isOnCFDomain() {
  return Boolean(location.hostname.match(/\.creativefabrica\.com|creativefabrica\.com/))
}

export function isOnMichaelsDomain() {
  return Boolean(
    location.hostname.match(
      /\.michaelsdigitaldownloads\.com|michaelsdigitaldownloads\.com|\.bobsanalog\.xyz|bobsanalog\.xyz/,
    ),
  )
}

function saveTokenToLocalStorage(token: string) {
  localStore.setString(TOKEN_LOCAL_STORAGE_KEY, token)
}

/**
 * This function works out what kind of authentication to use for the token.
 * If you are on a .creativefabrica.com or creativefabrica.com domain, it will
 * save the token to a cookie to enable cookie sharing.
 *
 * If that fails, we fallback to using a token.
 *
 * Since cookie sharing only works on the above domains, we have to use localStorage
 * to save the token on other domains.
 */

export function saveToken(token: string, origin: AuthOrigin) {
  if (isOnCFDomain() || isOnMichaelsDomain()) {
    // if saving the cookie fails, we can save to local storage as a fallback.
    return jwtAuth(token, origin).catch(() => {
      saveTokenToLocalStorage(token)
    })
  }

  // the bridge is skipped on non-CF/MD domains — record it so we can see the gap
  saveTokenToLocalStorage(token)
  reportJwtAuthOutcome(origin, 'SKIPPED_DOMAIN')

  return Promise.resolve()
}

export async function jwtAuth(token: string, origin: AuthOrigin, attempt = 1) {
  const headers = new Headers()

  headers.append('accept', 'application/json, multipart/mixed')
  headers.append('content-type', 'application/json')

  let res: Response
  try {
    res = await fetch(`${CF_URL}/cfsecure/jwtauth`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        token,
        origin,
      }),
    })
  } catch (error) {
    // network error / CORS / blocked request
    reportJwtAuthOutcome(origin, 'NETWORK_ERROR', undefined, attempt)
    if (attempt > 3) {
      reportJwtAuthOutcome(origin, 'RETRY_EXHAUSTED', undefined, attempt)
      throw error instanceof Error ? error : new Error('Failed to authenticate with JWT')
    }
    await sleep(2000)
    return jwtAuth(token, origin, attempt + 1)
  }

  if (!res.ok) {
    reportJwtAuthOutcome(origin, 'HTTP_ERROR', res.status, attempt)
    if (attempt > 3) {
      reportJwtAuthOutcome(origin, 'RETRY_EXHAUSTED', res.status, attempt)
      throw new Error('Failed to authenticate with JWT')
    }

    await sleep(2000)
    return jwtAuth(token, origin, attempt + 1)
  }

  reportJwtAuthOutcome(origin, 'OK', res.status, attempt)
}

// oxlint-disable-next-line require-await
export async function logOut() {
  localStore.remove(TOKEN_LOCAL_STORAGE_KEY)

  try {
    const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN
    deleteCookie(WORDPRESS_LOGIN_COOKIE_NAME, '/', cookieDomain)
  } catch {}
}
